import { Box, VStack, Heading, Text, HStack, Badge, Image, Button, useToast } from '@chakra-ui/react';
import { StarIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function TeacherProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const fetchTeacherAndReviews = async () => {
      try {
        // Convert slug back to name for query
        const name = slug.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');

        // First try to find an exact match
        let { data: teacherData, error: teacherError } = await supabase
          .from('public_teachers')
          .select('*')
          .eq('full_name', name)
          .single();

        // If no exact match, try to find the first teacher with this name
        if (teacherError && teacherError.code === 'PGRST116') {
          const { data: teachers, error: teachersError } = await supabase
            .from('public_teachers')
            .select('*')
            .ilike('full_name', name)
            .order('created_at', { ascending: true })
            .limit(1);

          if (teachersError) throw teachersError;
          teacherData = teachers[0];
        } else if (teacherError) {
          throw teacherError;
        }

        if (!teacherData) {
          setError('Teacher not found');
          setLoading(false);
          return;
        }

        setTeacher(teacherData);

        // Only fetch reviews if we have a valid teacher
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('*')
          .eq('teacher_id', teacherData.id)
          .order('created_at', { ascending: false });

        if (reviewsError) {
          console.error('Error fetching reviews:', reviewsError);
          setReviews([]);
        } else {
          setReviews(reviewsData || []);
        }
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        setError('Failed to load teacher profile');
        toast.closeAll();
        toast({
          title: 'Error',
          description: 'Failed to load teacher profile',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherAndReviews();
  }, [slug, toast]);

  const formatRating = (rating) => {
    return rating.toFixed(1);
  };

  const formatPrice = (rate, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(rate);
  };

  if (loading) {
    return (
      <Box p={8}>
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Text>{error}</Text>
      </Box>
    );
  }

  return (
    <Box p={8} maxW="4xl" mx="auto">
      <Button
        leftIcon={<ArrowBackIcon />}
        variant="ghost"
        mb={6}
        onClick={() => navigate(-1)}
      >
        Back to Teachers
      </Button>

      <VStack spacing={8} align="stretch">
        <HStack spacing={6}>
          <Image
            src={teacher.headshot_url || 'https://via.placeholder.com/200'}
            alt={teacher.full_name}
            borderRadius="full"
            boxSize="200px"
            objectFit="cover"
          />
          <VStack align="start" spacing={3} flex={1}>
            <Heading size="xl" color="teal.800">{teacher.full_name}</Heading>
            <Badge colorScheme="teal" fontSize="md" p={2}>{teacher.specialty}</Badge>
            <Text fontSize="2xl" fontWeight="bold" color="teal.600">
              {formatPrice(teacher.rate, teacher.currency)}/hour
            </Text>
            {teacher.review_count > 0 && (
              <HStack>
                <StarIcon color="yellow.400" boxSize={5} />
                <Text fontWeight="medium" fontSize="lg">{formatRating(teacher.rating)}/5.0</Text>
                <Text color="gray.500">({teacher.review_count} reviews)</Text>
              </HStack>
            )}
          </VStack>
        </HStack>

        <Box>
          <Heading size="md" mb={4}>About</Heading>
          <Text fontSize="lg" color="gray.700">{teacher.bio}</Text>
        </Box>

        {reviews.length > 0 && (
          <Box>
            <Heading size="md" mb={6}>Reviews</Heading>
            <VStack spacing={6} align="stretch">
              {reviews.map((review) => (
                <Box key={review.id} p={6} borderWidth="1px" borderRadius="lg">
                  <HStack mb={3}>
                    <StarIcon color="yellow.400" />
                    <Text fontWeight="medium">{formatRating(review.rating)}/5.0</Text>
                    <Text color="gray.500" fontSize="sm">
                      {new Date(review.created_at).toLocaleDateString()}
                    </Text>
                  </HStack>
                  <Text fontSize="md">{review.comment}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

export default TeacherProfile; 